import { cx } from '@emotion/css';
import {
  forwardRef,
  useCallback,
  KeyboardEvent,
  ChangeEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useController, useFormContext } from 'react-hook-form';

import callAllFn from '@julofinance/web-helpers/dist/fn/callAllFn';
import mergeRefs from '@julofinance/web-helpers/dist/fn/mergeRefs';

import { removeNonNumber } from '@utils/number';

import FieldGroup from '../field-group';
import Input from '../input/InputBase';

import { BankAccountInputProps } from './types';
import { bankAccountInputCx, bankInputWarning } from './styles';
import { Button, Typography } from '@julo-ui/react';
import { CheckCircle } from '@new-components/shapes';
import ExclamationIcon from '@new-components/shapes/ExclamationIcon';

const BankAccountInput = forwardRef<HTMLDivElement, BankAccountInputProps>(
  (props, ref) => {
    const {
      name,
      label,
      helperText,
      className,
      inputProps,
      isVerified = false,
      isInvalid = false,
      isValidated = false,
      onKeyDown,
      disabled,
      value,
      defaultValue,
      rules,
      required,
      inputRef = null,
      onChange,
      onBlur,
      withNotes = true,
      ...resProps
    } = props;
    const [isFocus, setIsFocus] = useState(false);

    const { control, trigger } = useFormContext();
    const {
      fieldState: { error, invalid },
      field: {
        ref: controlRef,
        onChange: controllerOnChange,
        onBlur: controllerOnBlur,
        value: controllerValue,
      },
    } = useController({
      name,
      control,
      defaultValue,
      rules: {
        ...(required && { required: 'This field is required' }),
        ...(rules && rules),
      },
    });

    const prevIsValidated = useRef(isValidated);

    const handleOnKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
      if (
        e.key.length > 1 ||
        ((e.altKey || e.shiftKey || e.ctrlKey || e.metaKey) &&
          e.key.length <= 1)
      )
        return;
      if (/[^a-zA-Z0-9]/.test(e.key) || /^[a-zA-Z]$/.test(e.key))
        return e.preventDefault();
    }, []);

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        e.target.setAttribute('data-bank-account-invalid', 'false');
        e.target.setAttribute('data-bank-account-verified', 'false');
        e.target.setAttribute('data-bank-account-validated', 'false');
        e.target.value = removeNonNumber(e.target.value);
      },
      [],
    );

    const offFocus = () => setIsFocus(false);

    useEffect(() => {
      if (prevIsValidated.current !== isValidated) {
        trigger(name);
        prevIsValidated.current = isValidated;
      }
    }, [isValidated, name, trigger]);

    return (
      <FieldGroup
        invalid={invalid}
        error={error}
        helperText={helperText}
        className={cx(bankAccountInputCx, className)}
        ref={ref}
        name={name}
        footer={
          isFocus &&
          withNotes && (
            <div className={bankInputWarning}>
              <div>
                <ExclamationIcon />
                <Typography type='caption'>
                  Pastikan nomor rekening milik pribadi
                </Typography>
              </div>
              <div>
                <ExclamationIcon />
                <Typography type='caption'>
                  Pastikan rekening ini juga terdaftar di Grab
                </Typography>
              </div>
              <div>
                <ExclamationIcon />
                <Typography type='caption'>
                  Pastikan ada transaksi di rekeningmu dalam 3 bulan terakhir
                </Typography>
              </div>
            </div>
          )
        }
      >
        <div className='bank-account-wrapper'>
          <Input
            {...resProps}
            ref={ref}
            className='bank-account-input'
            inputProps={{
              ...inputProps,
              'data-bank-account-invalid': isInvalid,
              'data-bank-account-verified': isVerified,
              'data-bank-account-validated': isValidated,
              'data-bank-account-error-message': error?.message,
            }}
            inputRef={mergeRefs(controlRef, inputRef)}
            isInvalid={invalid}
            value={value ?? controllerValue}
            onChange={callAllFn(handleChange, onChange, controllerOnChange)}
            onBlur={callAllFn(onBlur, controllerOnBlur, offFocus)}
            onKeyDown={callAllFn(handleOnKeyDown, onKeyDown)}
            disabled={disabled}
            label={label}
            name={name}
            onFocus={() => setIsFocus(true)}
          />
          {isVerified && (
            <Button
              disabled
              variant='secondary'
              className='bank-account-verify-btn'
            >
              <CheckCircle width={20} height={20} className='check-icon' />
              <span>Terverifikasi</span>
            </Button>
          )}
        </div>
      </FieldGroup>
    );
  },
);

export default BankAccountInput;
